import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import FeatureToggle from '@components/FeatureToggle';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import Input from '@components/Input';
import Tip from '@components/Tip';
import Title from '@components/Title';
import { Validator } from '@components/Validator';
import useInputHandlers from '@hooks/useInputHandelrs';
import useLangString from '@hooks/useLangString';
import useTourEditing from '@hooks/useTourEditing';
import { $templateId } from '@stores/dataStores';
import {
  $GateEditingAuthor,
  $GateEditingCustomIcons,
  $editingFirstSceneButton,
  $editingFlag,
} from '@stores/editingStores';
import {
  type EditConfigAuthor,
  type EditConfigCustomIcons,
  type EditConfigUI,
  type GlobalFlags,
  ImageType,
  MediaType,
  type SceneConfigFirstSceneButton,
  TemplateId,
} from '@types';
import colorObjToString from '@utils/colorObjToString';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import { getTemplateConfig } from '@utils/templateConfig';
import { useGate, useStore } from 'effector-react';
import hexRgb from 'hex-rgb';
import { flagToBool, toFlag } from 'pano-utils';
import { useEffect, useMemo, useState } from 'react';
import { selectLibCBIcons } from '../../mediaLib';
import {
  customIconDefaultMap,
  customIconLegacyDefaultMap,
} from '../../player/constants';
import PanelBase from '../PanelBase';
import type { PanelBaseProps } from '../types';

export const flagDefaultGlobal: GlobalFlags = {
  showAuthorName: 1,
  showTourPV: 1,
  showTourRemark: 1,
  showSceneName: 1,
  showFullscreenBtn: 1,
  showVRBtn: 1,
  showViewSwitchBtn: 1,
  showGyroBtn: 1,
  defaultEnabledGyro: 0,
  dragMode: 1,
  showAutoRotateBtn: 1,
  enableBouncing: 1,
  showMap: 1,
  showHotspotBtn: 0,
  forceLandscape: 0,
  compareMode: 0,
};

export const flagKeysGlobal = Object.keys(
  flagDefaultGlobal
) as (keyof GlobalFlags)[];

export const customIconDefaultGlobal: EditConfigCustomIcons = {
  remarkBtn: {},
};

export const customIconKeysGlobal = Object.keys(
  customIconDefaultGlobal
) as (keyof EditConfigCustomIcons)[];

const authorTypeItems: DropdownItem<EditConfigAuthor['mode']>[] = [
  { id: 0, name: '使用账户名称' },
  { id: 1, name: '使用归属人姓名' },
  { id: 2, name: '自定义昵称' },
];

function GlobalBase({
  defaultValue = {},
  onConfirm,
}: PanelBaseProps<{
  flag?: GlobalFlags;
  firstSceneButton?: SceneConfigFirstSceneButton;
  customIcons?: EditConfigCustomIcons;
  canvasBgColor?: EditConfigUI['canvasBgColor'];
  author?: EditConfigAuthor;
}>) {
  const templateId = useStore($templateId);
  const {
    flag: defaultFlag,
    firstSceneButton: defaultFirstSceneButton,
    customIcons: defaultCustomIcons,
    canvasBgColor: defaultCanvasBgColor = '#000000',
    author: defaultAuthor,
  } = defaultValue;
  const flag = useTourEditing(defaultFlag, flagDefaultGlobal);
  const customIcons = useTourEditing(
    defaultCustomIcons,
    customIconDefaultGlobal
  );
  const globalFlagsSettings = getTemplateConfig(templateId)(
    'globalFlagsSettings'
  );
  const firstSceneButton = useTourEditing(defaultFirstSceneButton, {
    enabled: 0,
    label: '',
    fontColor: '255,255,255,1',
    bgColor: '0,0,0,1',
  });
  const author = useTourEditing(defaultAuthor, {
    mode: 0,
    customNickname: '',
  });

  const customAuthorNameHandler = useInputHandlers((val) => {
    author.setState((draft) => {
      draft.customNickname = val;
    });
  });

  const lang = useLangString(
    {
      firstSceneButtonPlaceholder: ['返回', 'Home'],
    },
    []
  );
  const [canvasBgColor, setCanvasBgColor] = useState(defaultCanvasBgColor);

  useEffect(() => {
    $editingFlag.api.set(flag.state);
    return () => {
      $editingFlag.api.set(null);
    };
  }, [flag.state]);

  useEffect(() => {
    $editingFirstSceneButton.api.set(firstSceneButton.state);
    return () => {
      $editingFirstSceneButton.api.set(null);
    };
  }, [firstSceneButton.state]);

  useGate($GateEditingCustomIcons, customIcons.state);
  useGate($GateEditingAuthor, author.state);

  const getToggleProps = (key: keyof GlobalFlags) => {
    return {
      isOn: !!flag.state[key],
      onToggle: (bool: boolean) => {
        flag.setState((draft) => {
          draft[key] = toFlag(bool);
        });
      },
    };
  };

  const canvasColorString = useMemo(() => {
    const { red: r, green: g, blue: b } = hexRgb(canvasBgColor);
    return colorObjToString({ r, g, b });
  }, [canvasBgColor]);

  const handleIconSelect = async () => {
    const { libItems, sysItems } = await selectLibCBIcons();
    if (sysItems) {
      customIcons.setState((draft) => {
        draft.remarkBtn = {
          type: MediaType.system,
          mediaId: 0,
          mediaUrl: sysItems[0].url,
        };
      });
    } else if (libItems) {
      customIcons.setState((draft) => {
        draft.remarkBtn = {
          type: MediaType.lib,
          mediaId: libItems[0].id,
          mediaUrl: libItems[0].url,
        };
      });
    }
  };

  const handleConfirm = () => {
    onConfirm({
      flag: flag.state,
      firstSceneButton: firstSceneButton.state,
      customIcons: customIcons.state,
      canvasBgColor,
      author: author.state,
    });
  };

  const remarkSetting = () => {
    if (!globalFlagsSettings.remark) return null;
    const remarkIconEnabled =
      globalFlagsSettings.remark.icon && !!flag.state.showTourRemark;
    const remarkMapButtonEnabled =
      globalFlagsSettings.remark.map && !!flag.state.showTourRemark;
    return (
      <>
        <FeatureToggle title="作品简介" {...getToggleProps('showTourRemark')} />
        {remarkIconEnabled && (
          <>
            <ImageContainer
              className="mt-20 h-70"
              useResetIcon
              onDeleteClick={() => {
                customIcons.setState((draft) => {
                  draft.remarkBtn = undefined;
                });
              }}
              placeholder={
                <Image
                  className="h-40 w-40"
                  src={
                    templateId === TemplateId.legacy
                      ? customIconLegacyDefaultMap.remarkBtn
                      : customIconDefaultMap.remarkBtn
                  }
                  type={ImageType.sysMedia}
                />
              }
            >
              {!!customIcons.state.remarkBtn?.mediaUrl && (
                <Image
                  src={customIcons.state.remarkBtn.mediaUrl}
                  type={
                    customIcons.state.remarkBtn?.type === MediaType.lib
                      ? ImageType.media
                      : ImageType.sysMedia
                  }
                  className="h-40 w-40"
                />
              )}
            </ImageContainer>
            <div className="mt-20 flex items-center justify-between">
              <Tip>建议尺寸：80 X 80</Tip>
              <Button size="sm" onClick={handleIconSelect}>
                编辑图标
              </Button>
            </div>
          </>
        )}
        {remarkMapButtonEnabled && (
          <>
            <FeatureToggle
              title="简介中显示“足迹”"
              {...getToggleProps('showMap')}
            />
            <Tip className="mt-10">足迹即场景的GPS位置，可在素材库完善</Tip>
          </>
        )}
      </>
    );
  };

  const authorSetting = () => {
    return (
      <>
        <div className="mt-20">昵称设置</div>
        <Dropdown
          className="mt-10"
          selected={author.state.mode}
          items={authorTypeItems}
          onChange={(val) => {
            author.setState((draft) => {
              draft.mode = val;
              draft.customNickname = '';
            });
          }}
        />
        {author.state.mode === 2 && (
          <Validator
            value={author.state.customNickname}
            warningMessage="请输入自定义昵称"
            validFn={(value = '') => !!value.trim()}
          >
            {({ value, warning }) => (
              <Input
                scheme="dark"
                className="h-40 mt-10"
                placeholder="请输入自定义昵称"
                value={value}
                warning={warning}
                maxLength={50}
                {...customAuthorNameHandler}
              />
            )}
          </Validator>
        )}
      </>
    );
  };

  return (
    <PanelBase
      title={<Title>全局开关</Title>}
      isChanged={
        flag.isChanged || firstSceneButton.isChanged || customIcons.isChanged
      }
      onHelpClick={() => {}}
      onConfirm={handleConfirm}
    >
      <Collapse title="信息展示">
        <FeatureToggle
          title="创作者昵称"
          isOn={!!flag.state.showAuthorName}
          onToggle={(bool) => {
            flag.setState((draft) => {
              draft.showAuthorName = toFlag(bool);
            });
            author.setState((draft) => {
              draft.mode = 0;
              draft.customNickname = '';
            });
          }}
        />
        {!!flag.state.showAuthorName && authorSetting()}
        <FeatureToggle title="作品访问数" {...getToggleProps('showTourPV')} />
        <FeatureToggle
          title={
            <div>
              场景名称
              <Tip as="span" className="ml-5">
                (切换时显示）
              </Tip>
            </div>
          }
          {...getToggleProps('showSceneName')}
        />
        {remarkSetting()}
      </Collapse>
      <Collapse title="辅助功能设置">
        <FeatureToggle
          title={
            <div>
              全屏
              <Tip as="span" className="ml-5">
                (仅PC和安卓端有效）
              </Tip>
            </div>
          }
          {...getToggleProps('showFullscreenBtn')}
        />
        <FeatureToggle title="VR模式" {...getToggleProps('showVRBtn')} />
        <FeatureToggle
          title="视角切换选择"
          {...getToggleProps('showViewSwitchBtn')}
        />
        <FeatureToggle
          title="热点显示/隐藏开关"
          {...getToggleProps('showHotspotBtn')}
        />
        <Tip className="mt-10">开启后可以切换热点/标尺等的显示/隐藏</Tip>
        <FeatureToggle
          title={
            <div>
              陀螺仪图标
              <Tip as="span" className="ml-5">
                (仅手机端有效）
              </Tip>
            </div>
          }
          {...getToggleProps('showGyroBtn')}
        />
        <Tip className="mt-10">开启后作品中显示陀螺仪功能图标</Tip>
        <FeatureToggle
          title={
            <div>
              陀螺仪功能
              <Tip as="span" className="ml-5">
                (仅手机端有效）
              </Tip>
            </div>
          }
          {...getToggleProps('defaultEnabledGyro')}
        />
        <Tip className="mt-10">开启后打开作品时默认启用陀螺仪功能</Tip>
        <FeatureToggle
          title={
            <div>
              缩放回弹效果
              <Tip as="span" className="ml-5">
                (仅手机端有效）
              </Tip>
            </div>
          }
          {...getToggleProps('enableBouncing')}
        />
        <Tip className="mt-10">开启后场景放到最大时会有回弹效果</Tip>
        <FeatureToggle
          title={
            <div>
              手机横屏播放
              <Tip as="span" className="ml-5">
                (仅手机端有效）
              </Tip>
            </div>
          }
          {...getToggleProps('forceLandscape')}
        />
        <Tip className="mt-10">
          开启后手机端打开作品时会引导用户横屏观看作品，非横屏无法观看
        </Tip>
        <FeatureToggle
          title="场景对比功能"
          {...getToggleProps('compareMode')}
        />
        <Tip className="mt-10">
          开启后打开作品时如果当前场景设置过对比场景将默认进入对比模式
        </Tip>
        <div className="mt-20 text-sm text-orange">
          注：VR模式/视角切换/陀螺仪功能仅对全景图片/全景视频有效
        </div>
      </Collapse>
      {!!globalFlagsSettings.backToFirstScene && (
        <Collapse title="返回首场景设置">
          <FeatureToggle
            title="返回首场景功能"
            isOn={flagToBool(firstSceneButton.state.enabled ?? 0)}
            onToggle={(value) => {
              firstSceneButton.setState((draft) => {
                draft.enabled = toFlag(!!value);
              });
            }}
          />
          <Tip className="mt-10">开启后在非首场景显示返回功能图标</Tip>
          {firstSceneButton.state.enabled === 1 && (
            <>
              <div className="mt-20 mb-10">按钮名称</div>
              <Input
                maxLength={50}
                scheme="dark"
                className="h-30"
                placeholder={lang.firstSceneButtonPlaceholder}
                value={firstSceneButton.state.label}
                onChange={(event) => {
                  firstSceneButton.setState((draft) => {
                    draft.label = event.target.value;
                  });
                }}
              />
              <ColorPicker
                className="mt-20"
                colorString={firstSceneButton.state.fontColor}
                onChange={(value) => {
                  firstSceneButton.setState((draft) => {
                    draft.fontColor = value;
                  });
                }}
              >
                <ColorPicker.Header>
                  按钮文字颜色：
                  <ColorPicker.Reset defaultColor="255,255,255,1" />
                </ColorPicker.Header>
                <ColorPicker.Bar />
              </ColorPicker>
              <ColorPicker
                className="mt-20"
                colorString={firstSceneButton.state.bgColor}
                onChange={(value) => {
                  firstSceneButton.setState((draft) => {
                    draft.bgColor = value;
                  });
                }}
              >
                <ColorPicker.Header>
                  按钮背景颜色：
                  <ColorPicker.Reset defaultColor="0,0,0,1" />
                </ColorPicker.Header>
                <ColorPicker.Bar />
              </ColorPicker>
            </>
          )}
        </Collapse>
      )}
      <Collapse title="PC端操作模式设置">
        <Dropdown
          selected={flag.state.dragMode}
          items={[
            { id: 0, name: '反向拖拽观看' },
            { id: 1, name: '正向拖拽观看' },
          ]}
          onChange={(id) => {
            flag.setState((draft) => {
              draft.dragMode = toFlag(!!id);
            });
          }}
        />
      </Collapse>
      <Collapse title="页面背景">
        <ColorPicker
          className="mt-20"
          colorString={canvasColorString}
          onChange={(str) => {
            const { hex } = colorStringToHexAndAlpha(str);
            setCanvasBgColor(hex);
          }}
        >
          <ColorPicker.Header>
            <div>背景颜色：</div>
            <ColorPicker.Reset defaultColor="0,0,0,1" />
          </ColorPicker.Header>
          <ColorPicker.Bar disableAlpha />
        </ColorPicker>
        <Tip className="mt-10 text-orange">
          注：对于高清矩阵场景，通过设置页面背景颜色可以更契合高清矩阵场景的展示，该设置需保存后刷新页面方可查看效果
        </Tip>
      </Collapse>
    </PanelBase>
  );
}

export default GlobalBase;
